import React, { useMemo, useState } from 'react'
import { footerLine } from '../../copy'
import type { FfpDashboardData, PeriodKey } from '../../types'
import { peopleHave, shareOf, sincePhrase, sliceSeries } from '../../utils'
import { AboutPanel } from '../AboutPanel'
import { Reveal } from '../Reveal'
import { Intro, SectionHeader } from '../SectionHeader'
import { AppCard } from './AppCard'
import { CoachingCard } from './CoachingCard'
import styles from './engagement.module.sass'
import { EngagementChartCard } from './EngagementChartCard'
import { EngagementPanelContent, PANEL_TITLES, PanelKey } from './panels'
import { sinceStartMonths } from './utils'
import { WorkshopsCard } from './WorkshopsCard'

type Props = { data: FfpDashboardData; period: PeriodKey }

export const Engagement = ({ data, period }: Props) => {
  const [panel, setPanel] = useState<PanelKey | null>(null)
  const points = useMemo(() => sliceSeries(data.engagement.series, period), [data, period])
  const since = sincePhrase(data.first_month)
  const counted = `${data.people.enrolled} enrolled`
  // Only the chart follows the period. The coaching, workshops and app
  // figures count everyone since the start, whatever the period.
  const chartFooter = footerLine(data, points, counted)
  const programmeFooter = footerLine(data, sinceStartMonths(data.first_month, data.engagement.series), counted)
  const workshopsFooter = footerLine(data, sinceStartMonths(data.engagement.workshops.first_month, data.engagement.series), counted)

  const openEngagement = () => setPanel('engagement')
  const openCoaching = () => setPanel('coaching')
  const openWorkshops = () => setPanel('workshops')
  const openApp = () => setPanel('app')
  const closePanel = () => setPanel(null)

  return (
    <section id="engagement" className={styles.section}>
      <Reveal>
        <SectionHeader title={'Your team’s engagement with the programme'} intro={engagementIntro(data)} />
      </Reveal>
      <div className={styles.grid}>
        <EngagementChartCard data={data} points={points} since={since} footer={chartFooter} highlighted={panel === 'engagement'} onAbout={openEngagement} />
        <Reveal>
          <CoachingCard data={data} points={points} since={since} footer={programmeFooter} highlighted={panel === 'coaching'} onAbout={openCoaching} />
        </Reveal>
        <Reveal>
          <WorkshopsCard data={data} period={period} footer={workshopsFooter} highlighted={panel === 'workshops'} onAbout={openWorkshops} />
        </Reveal>
        <Reveal>
          <AppCard data={data} points={points} since={since} footer={programmeFooter} highlighted={panel === 'app'} onAbout={openApp} />
        </Reveal>
      </div>
      <AboutPanel open={panel !== null} title={panel ? PANEL_TITLES[panel] : ''} onClose={closePanel}>
        <EngagementPanelContent panel={panel} data={data} points={points} period={period} />
      </AboutPanel>
    </section>
  )
}

const engagementIntro = ({ people, engagement }: FfpDashboardData): Intro => {
  if ((shareOf(people.used_any_part, people.enrolled) ?? 0) > 50) {
    return { before: 'Most enrolled people have ', highlight: 'used at least one part', after: ' of the programme.' }
  }

  const onboarded = `${peopleHave(people.onboarded)} onboarded so far`
  if (engagement.app.claimed > 0) {
    return { before: `${onboarded}, and `, highlight: `${peopleHave(engagement.app.claimed)} set up the app`, after: '.' }
  }
  return { before: '', highlight: onboarded, after: '.' }
}
